import './Game.css'

import { Model, User } from '@kfiroo/backgammon-core'
import { doc, DocumentReference } from 'firebase/firestore'
import React, { useEffect } from 'react'

import { useFirestore } from '../firebase/FirebaseProvider'
import { useFirestoreDocData } from '../firebase/hooks'
import monkey from '../images/monkey.jpeg'
import { useToast } from './components/Toast'
import { CreatedGame } from './CreatedGame'
import { GameOver } from './GameOver'
import { InProgressGame } from './InProgressGame'
import { resolveGameApi } from './services/gameApiFactory'

interface GameModelResult {
  status: 'loading' | 'success' | 'error'
  error?: Error
  model?: Model
  ref: DocumentReference
}
function useGameModel(gameId: string): GameModelResult {
  const firestore = useFirestore()
  const ref = doc(firestore, 'games', gameId)
  const { status, error, data } = useFirestoreDocData(ref)

  if (status === 'success') {
    const model = data as Model
    return { status, error, model, ref }
  }

  return { status, error, model: undefined, ref }
}

interface GameProps {
  user: User
  gameId: string
}

export function Game({ gameId, user }: GameProps) {
  const { status, error, model, ref } = useGameModel(gameId)
  const { showError } = useToast()

  useEffect(() => {
    if (status === 'error') {
      console.error('load game info error', error)
      showError('Could not load the game — try again')
    }
  }, [status, error, showError])

  if (status === 'loading') {
    return <div>Loading game info...</div>
  }

  return <GameInner user={user} model={model} gameRef={ref} error={error} />
}

function GameInner({
  user,
  model,
  gameRef,
  error,
}: {
  user: User
  model?: Model
  gameRef: DocumentReference
  error?: Error
}) {
  const resolved = resolveGameApi(gameRef, model, user)
  switch (resolved.kind) {
    case 'created':
      return <CreatedGame gameApi={resolved.api} />
    case 'game-over':
      return <GameOver gameApi={resolved.api} user={user} />
    case 'in-progress':
      return (
        <InProgressGame
          key={
            model?.gameState.state === 'in-progress'
              ? model.gameState.turn
              : undefined
          }
          gameApi={resolved.api}
        />
      )
  }
  return (
    <div className="monkey">
      <h1>Game not found</h1>
      <img src={monkey} alt="monkey" />
      <button
        style={{ width: 200 }}
        onClick={() => window.location.assign(`/`)}
      >
        Try again
      </button>
      {error && (
        <div style={{ color: 'red', position: 'absolute', top: '100vh' }}>
          <p>load game info error</p>
          <p>{error.message}</p>
        </div>
      )}
    </div>
  )
}
